import first from "lodash/first";
import last from "lodash/last";
import sortBy from "lodash/sortBy";
import { RadioItem } from "PFComponents/radios";
import { utc } from "PFCore/helpers/date";
import { useDateFormatter } from "PFCore/hooks";
import { Booking } from "PFTypes";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useFutureBookings } from "../../hooks/use_future_bookings";
import { BookingSelectOption } from "../booking_questions/booking_questions.types";
import { OptionLabel } from "./option_label";

export const useBookingSelectOptions = ({
  booking,
  activityId,
  bookingTemplateId
}: {
  booking: Booking;
  activityId?: number;
  bookingTemplateId?: number;
}) => {
  const { t } = useTranslation("bookingModule");
  const { formatDate } = useDateFormatter();

  const { bookings, isFetching } = useFutureBookings({
    thisBooking: { id: booking.id, startDate: booking.start_date },
    activityId: activityId ?? undefined,
    bookingTemplateId: bookingTemplateId ?? undefined,
    profileId: booking.profile_id
  });

  const bookingsOrderedByStartDate = useMemo(
    () => sortBy(bookings?.entries || [], (booking) => new Date(booking.start_date).getTime()),
    [bookings]
  );
  const deleteOptions: RadioItem<BookingSelectOption>[] = [
    {
      id: BookingSelectOption.This,
      label: (
        <OptionLabel
          label={t("bookings.delete.options.this")}
          startDate={formatDate(utc(booking.start_date))}
          endDate={formatDate(utc(booking.end_date))}
        />
      )
    },
    {
      id: BookingSelectOption.Future,
      label: (
        <OptionLabel
          label={t("bookings.delete.options.future")}
          startDate={formatDate(utc(booking.start_date))}
          endDate={formatDate(utc(last(bookingsOrderedByStartDate)?.end_date))}
        />
      )
    },
    {
      id: BookingSelectOption.All,
      label: (
        <OptionLabel
          label={t("bookings.delete.options.all")}
          startDate={formatDate(utc(first(bookingsOrderedByStartDate)?.start_date))}
          endDate={formatDate(utc(last(bookingsOrderedByStartDate)?.end_date))}
        />
      )
    }
  ];

  const updateOptions: RadioItem<BookingSelectOption>[] = deleteOptions.slice(1, 3);

  return { deleteOptions, updateOptions, isFetching };
};
